import React, { useRef } from 'react';
import { MonitorIcon, ServerIcon, SearchIcon, TrendingUpIcon, CompassIcon, MailIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
export const Services: React.FC = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const {
        clientWidth
      } = scrollRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth : clientWidth;
      scrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const services = [{
    title: 'CorePrint',
    description: 'Data-gedreven klantacquisitie voor B2B-bedrijven. Eerst bewijs, dan bouwen.',
    icon: <MonitorIcon className="h-6 w-6" data-id="element-600" />,
    link: 'https://coreprint.nl/',
    isHighlighted: true,
    cta: 'Vraag een offerte aan'
  }, {
    title: 'Hosting',
    description: 'Betrouwbare en snelle hosting voor uw website, inclusief technisch beheer en backups.',
    icon: <ServerIcon className="h-6 w-6" data-id="element-601" />,
    link: '/hosting'
  }, {
    title: 'SEO',
    description: 'Optimalisatie voor zoekmachines zodat klanten uw bedrijf beter kunnen vinden in Google.',
    icon: <SearchIcon className="h-6 w-6" data-id="element-602" />,
    link: '/seo'
  }, {
    title: 'Google Ads',
    description: 'Slimme advertenties als datamachine om te meten wat werkt in jouw markt.',
    icon: <TrendingUpIcon className="h-6 w-6" data-id="element-603" />,
    link: '/google-ads'
  }, {
    title: 'Strategie & Advies',
    description: 'Een complete online groeistrategie afgestemd op jouw markt, doelgroep en doelen.',
    icon: <CompassIcon className="h-6 w-6" data-id="element-604" />,
    link: '/strategie'
  }, {
    title: 'E-mail marketing',
    description: 'Professionele e-mailoplossingen en campagnes die leads omzetten in klanten.',
    icon: <MailIcon className="h-6 w-6" data-id="element-605" />,
    link: '/email'
  }, {
    title: 'Webdesign Alkmaar',
    description: 'Professionele websites die perfect aansluiten bij uw bedrijf en direct nieuwe klanten opleveren.',
    icon: <MonitorIcon className="h-6 w-6" data-id="element-606" />,
    link: 'https://webdesignalkmaar.nl/'
  }];
  return <div className="w-full bg-white py-20 md:py-24 overflow-hidden" data-id="element-607">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-608">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-609">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-610">
            Wat bieden we aan?
          </motion.h2>
        </div>

        {/* Carousel */}
        <div className="relative mb-20 group" data-id="element-611">
          <div ref={scrollRef} className="flex overflow-x-auto pb-8 snap-x snap-mandatory hide-scrollbar gap-6 scroll-smooth" data-id="element-612">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`min-w-[100%] sm:min-w-[calc(50%-12px)] lg:min-w-[calc(33.333%-16px)] snap-start flex flex-col rounded-xl p-8 h-full border ${service.isHighlighted ? 'bg-[#1F222C] text-white border-transparent shadow-xl' : 'bg-white text-[#111827] border-gray-100 shadow-sm hover:shadow-md transition-shadow'}`} data-id="element-613">
                <div className={`mb-6 ${service.isHighlighted ? 'text-white' : 'text-[#007AFF]'}`} data-id="element-614">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-615">{service.title}</h3>
                <p className={`text-sm leading-relaxed mb-8 flex-grow ${service.isHighlighted ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-616">
                  {service.description}
                </p>

                {service.isHighlighted ? <a href={service.link} target="_blank" rel="noopener noreferrer" className="inline-block w-full text-center bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold py-3 px-4 rounded-lg transition-colors text-sm" data-id="element-617">
                    {service.cta}
                  </a> : <a href={service.link} target={service.link.startsWith('http') ? '_blank' : '_self'} rel={service.link.startsWith('http') ? 'noopener noreferrer' : ''} className="text-[#007AFF] hover:text-[#005FCC] font-semibold text-sm transition-colors" data-id="element-618">
                    Meer informatie
                  </a>}
              </motion.div>)}
          </div>

          {/* Navigation Buttons */}
          <button onClick={() => scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-5 bg-white shadow-lg border border-gray-100 rounded-full p-3 text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-colors z-10 hidden md:block opacity-0 group-hover:opacity-100" aria-label="Vorige" data-id="element-619">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-620" />
          </button>
          <button onClick={() => scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-5 bg-white shadow-lg border border-gray-100 rounded-full p-3 text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-colors z-10 hidden md:block opacity-0 group-hover:opacity-100" aria-label="Volgende" data-id="element-621">
            <ChevronRightIcon className="w-6 h-6" data-id="element-622" />
          </button>
        </div>

        {/* Norden CTA card */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="bg-white rounded-2xl p-8 max-w-[900px] mx-auto shadow-xl border border-gray-100 relative overflow-hidden" data-id="element-623">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50 -mr-20 -mt-20" data-id="element-624"></div>

          <div className="flex flex-col md:flex-row items-center gap-8 relative z-10" data-id="element-625">
            <div className="md:w-2/3 text-center md:text-left" data-id="element-626">
              <h3 className="text-2xl font-bold text-[#111827] mb-3" data-id="element-627">
                Niet de dienst gevonden waarnaar je zoekt?
              </h3>
              <p className="text-[#6B7280] mb-6 text-lg" data-id="element-628">
                Neem contact op om te zien of we wat voor jou kunnen betekenen.
                We denken graag met je mee!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start" data-id="element-629">
                <a href="/contact" className="inline-block bg-[#007AFF] hover:bg-[#005FCC] text-white font-semibold py-3 px-8 rounded-xl transition duration-300 shadow-md text-center" data-id="element-630">
                  Doe de scan!
                </a>
                <a href="/contact" className="inline-block bg-white text-[#007AFF] font-semibold py-3 px-8 rounded-xl transition duration-300 text-center" data-id="element-631">
                  Neem contact op
                </a>
              </div>
            </div>

            <div className="md:w-1/3 flex justify-center relative" data-id="element-632">
              <motion.div whileHover={{
              rotate: [0, -5, 5, 0],
              scale: 1.05
            }} transition={{
              duration: 0.5
            }} className="relative" data-id="element-633">
                <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte - professionele website adviesbureau" className="w-48 h-48 object-contain drop-shadow-lg" data-id="element-634" />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>

      <style dangerouslySetInnerHTML={{
      __html: `
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `
    }} data-id="element-635" />
    </div>;
};